import { useMutation } from "@tanstack/react-query";
import { WebhookService } from "../../../services/api";
import FormTemplate from "../../templates/FormTemplate";
import { Button, Divider, Stack } from "@mui/material";
import FormInput from "../../molecules/FormInput";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { HttpHeader, WebhookEvent } from "../../../services/swagger";
import WebhookEventSelect from "../../molecules/WebhookEventSelect";
import useConfirm from "../../../hooks/useConfirm";
import { useNavigate } from "react-router";
import DeleteIcon from "@mui/icons-material/Delete";
import HttpHeaderList from "../../molecules/HttpHeaderList";
import WebhookLogs from "./WebhookLogs";

interface EditWebhookFormProps {
  _id: string;
}

export default function EditWebhookForm(props: EditWebhookFormProps) {
  const { _id } = props;
  const { t } = useTranslation("WebhookForm");
  const navigate = useNavigate();

  const [description, setDescription] = useState("");
  const [url, setUrl] = useState("");
  const [enabled, setEnabled] = useState(true);
  const [events, setEvents] = useState<WebhookEvent[]>([]);
  const [headers, setHeaders] = useState<HttpHeader[]>([]);
  const [logView, setLogView] = useState(false);

  const confirm = useConfirm();

  const { mutate: loadWebhook, isPending: isLoadingWebhook } = useMutation({
    mutationFn: async () => {
      const res = await WebhookService.getWebhook({ id: _id });
      return res.data.data;
    },
    onSuccess: (data) => {
      if (data) {
        setDescription(data.description);
        setUrl(data.url);
        setEnabled(data.enabled);
        setEvents(data.events);
        setHeaders(data.headers);
      }
    },
  });

  const { mutate: updateWebhook, isPending: isUpdating } = useMutation({
    mutationFn: async () => {
      const res = await WebhookService.updateWebhook({
        id: _id,
        updateWebhookBody: {
          description,
          url,
          enabled,
          events,
          headers,
        },
      });
      return res.data.data;
    },
  });

  const { mutate: deleteWebhook } = useMutation({
    mutationFn: async () => {
      const res = await WebhookService.deleteWebhook({ id: _id });
      return res.data.data;
    },
    onSuccess: () => {
      navigate(-1);
    },
  });

  useEffect(() => {
    if (_id) loadWebhook();
  }, [_id]);

  return (
    <FormTemplate
      title={t("EditWebhook")}
      onSubmit={() => updateWebhook()}
      disableHeaderCloseButton
      loading={isLoadingWebhook || isUpdating}
      options={[
        {
          label: t("Delete"),
          icon: <DeleteIcon color="error" />,
          onClick: () => confirm(deleteWebhook),
        },
      ]}
    >
      <Stack spacing={2}>
        <FormInput
          field="enabled"
          label={t("Enabled")}
          onChange={(v) => {
            setEnabled(v as boolean);
          }}
          type="switch"
          value={enabled}
        />
        <FormInput
          required
          label={t("Description")}
          field="description"
          placeholder={t("DescriptionPlaceholder")}
          value={description}
          onChange={(v) => setDescription(v as string)}
          type="string"
          loading={isLoadingWebhook || isUpdating}
        />
        <FormInput
          required
          label={t("Url")}
          field="url"
          placeholder={t("UrlPlaceholder")}
          value={url}
          onChange={(v) => setUrl(v as string)}
          type="string"
          loading={isLoadingWebhook || isUpdating}
        />
        <Stack direction="row" spacing={2}>
          <Button
            onClick={() => setLogView(false)}
            sx={{ width: "fit-content", px: 0 }}
            disabled={!logView}
          >
            {t("ViewConfigurations")}
          </Button>
          <Button
            onClick={() => setLogView(true)}
            sx={{ width: "fit-content", px: 0 }}
            disabled={logView}
          >
            {t("ViewLogs")}
          </Button>
        </Stack>
        <Divider />
        {logView ? (
          <WebhookLogs webhookId={_id} />
        ) : (
          <>
            <HttpHeaderList
              value={headers}
              onChange={(value) => setHeaders(value)}
            />
            <WebhookEventSelect
              value={events}
              onChange={(value) => setEvents(value)}
            />
          </>
        )}
      </Stack>
    </FormTemplate>
  );
}
